import React, { useEffect, useState } from "react";
import "../../styles/scss/salaryConfirm.scss";
import { useLoginCheck } from "../../hooks/useLoginCheck";

const apiUrl = process.env.REACT_APP_API_URL;
const yen = (n) => (typeof n === "number" ? `¥${n.toLocaleString()}` : "—");
const formatInstructorName = (i) => {
  if (i.first_name && i.last_name) {
    return `${i.username}（${i.first_name} ${i.last_name}）`;
  }
  return i.username;
};

const WEEKDAY_JP = ["日", "月", "火", "水", "木", "金", "土"];
const formatDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}/${m}/${day} (${WEEKDAY_JP[d.getDay()]})`;
};

// belong_student → [{ student, studio_name, lesson_name, plan_name, plan_fee }]
function flattenBelongStudent(belong) {
  const rows = [];
  if (!Array.isArray(belong)) return rows;
  for (const studioDict of belong) {
    for (const [studioName, lessonEntries] of Object.entries(studioDict || {})) {
      if (!Array.isArray(lessonEntries)) continue;
      for (const entry of lessonEntries) {
        for (const [lessonName, lessonData] of Object.entries(entry || {})) {
          if (
            !lessonData ||
            typeof lessonData !== "object" ||
            Array.isArray(lessonData) ||
            !("lesson" in lessonData)
          )
            continue;
          const planList = lessonData.lesson || [];
          for (const planDict of planList) {
            for (const [planName, planInfo] of Object.entries(planDict || {})) {
              const students = planInfo?.students || [];
              for (const student of students) {
                rows.push({
                  student,
                  studio_name: studioName,
                  lesson_name: lessonName,
                  plan_name: planName,
                  plan_fee: planInfo?.plan_fee,
                });
              }
            }
          }
        }
      }
    }
  }
  return rows;
}

// attendance_data → スタジオ/レッスン毎にまとめた [{ studio_name, lesson_name, rows: [{date, students}] }]
function groupAttendanceByLesson(attendanceData) {
  const groups = [];
  if (!Array.isArray(attendanceData)) return groups;
  for (const item of attendanceData) {
    const rows = [];
    for (const pair of item.lesson_students || []) {
      const [date, students] = pair;
      const filtered = (students || []).filter((s) => s !== "イントラ 用");
      if (filtered.length === 0) continue;
      rows.push({ date, students: filtered });
    }
    // 出席者なし・体験者のみの回（所属生徒の出席が無い日）も日付を表示する。
    // 生徒欄にはラベル（「出席者なし」「体験者のみ（N名）」）を出し、延べ人数には数えない。
    for (const pair of item.no_enrolled_lesson_details || []) {
      const [date, label, trialCount] = pair;
      const note =
        label === "体験者のみ" && trialCount
          ? `${label}（${trialCount}名）`
          : label;
      rows.push({ date, students: [], note });
    }
    if (rows.length === 0) continue;
    rows.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    groups.push({
      studio_name: item.studio_name,
      lesson_name: item.lesson_name,
      rows,
    });
  }
  return groups;
}

function SalaryConfirmPage() {
  useLoginCheck();

  const [instructors, setInstructors] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingMonths, setLoadingMonths] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [busy, setBusy] = useState(false);

  // 講師一覧取得
  useEffect(() => {
    fetch(`${apiUrl}/api/salary/instructor_list/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    })
      .then((r) => r.json())
      .then((data) => setInstructors(data.instructors || []))
      .catch((e) => console.error("講師一覧取得エラー:", e));
  }, []);

  // 月一覧取得
  useEffect(() => {
    if (!selectedInstructor) return;
    setLoadingMonths(true);
    setMonths([]);
    setSelectedMonth(null);
    setDetail(null);
    fetch(`${apiUrl}/api/salary/months/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ instructor_id: selectedInstructor.id }),
    })
      .then((r) => r.json())
      .then((data) => setMonths((data.months || []).reverse()))
      .catch((e) => console.error("月一覧取得エラー:", e))
      .finally(() => setLoadingMonths(false));
  }, [selectedInstructor]);

  // 明細取得（共通）
  const loadDetail = (recalculate = false) => {
    if (!selectedInstructor || !selectedMonth) return;
    setLoadingDetail(true);
    fetch(`${apiUrl}/api/salary/detail/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        instructor_id: selectedInstructor.id,
        year: selectedMonth.year,
        month: selectedMonth.month,
        recalculate,
      }),
    })
      .then((r) => r.json())
      .then((data) => setDetail(data))
      .catch((e) => console.error("明細取得エラー:", e))
      .finally(() => setLoadingDetail(false));
  };

  // 月選択時に明細取得
  useEffect(() => {
    if (selectedInstructor && selectedMonth) loadDetail(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstructor, selectedMonth]);

  // 確定 / 編集（同じAPI）
  const handleConfirm = async () => {
    if (!selectedInstructor || !selectedMonth) return;
    if (
      !window.confirm(
        `${selectedMonth.year}年${selectedMonth.month}月分を確定しますか？\n（既に確定済の場合は上書きされます）`
      )
    )
      return;
    setBusy(true);
    try {
      const res = await fetch(`${apiUrl}/api/salary/confirm/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          instructor_id: selectedInstructor.id,
          year: selectedMonth.year,
          month: selectedMonth.month,
        }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      // 月一覧と明細を再取得
      setMonths((prev) =>
        prev.map((m) =>
          m.year === selectedMonth.year && m.month === selectedMonth.month
            ? { ...m, confirmed: true }
            : m
        )
      );
      loadDetail(false);
    } catch (e) {
      alert(`確定に失敗しました: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // 編集 = 再計算した値を画面に出してから「確定」で上書き保存
  const handleEdit = () => loadDetail(true);

  // 確定取消
  const handleCancel = async () => {
    if (!selectedInstructor || !selectedMonth) return;
    if (
      !window.confirm(
        `${selectedMonth.year}年${selectedMonth.month}月分の確定を取消しますか？`
      )
    )
      return;
    setBusy(true);
    try {
      const res = await fetch(`${apiUrl}/api/salary/cancel/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          instructor_id: selectedInstructor.id,
          year: selectedMonth.year,
          month: selectedMonth.month,
        }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      setMonths((prev) =>
        prev.map((m) =>
          m.year === selectedMonth.year && m.month === selectedMonth.month
            ? { ...m, confirmed: false }
            : m
        )
      );
      loadDetail(false);
    } catch (e) {
      alert(`取消に失敗しました: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="management-salary-confirm">
      <h1>給与確定</h1>
      <div className="layout">
        {/* 講師リスト */}
        <div className="panel">
          <h3>講師</h3>
          {instructors.length === 0 && <div className="loading">読込中...</div>}
          {instructors.map((i) => (
            <div
              key={i.id}
              className={`list-item ${
                selectedInstructor?.id === i.id ? "selected" : ""
              }`}
              onClick={() => setSelectedInstructor(i)}
            >
              <span>{formatInstructorName(i)}</span>
            </div>
          ))}
        </div>

        {/* 月リスト */}
        <div className="panel">
          <h3>月</h3>
          {!selectedInstructor && (
            <div className="empty">講師を選択してください</div>
          )}
          {loadingMonths && <div className="loading">読込中...</div>}
          {months.map((m) => (
            <div
              key={`${m.year}-${m.month}`}
              className={`list-item ${
                selectedMonth?.year === m.year && selectedMonth?.month === m.month
                  ? "selected"
                  : ""
              }`}
              onClick={() => setSelectedMonth(m)}
            >
              <span>
                {m.year}年{m.month}月
              </span>
              {m.confirmed && <span className="badge">確定済</span>}
            </div>
          ))}
        </div>

        {/* 明細 */}
        <div className="detail">
          {!selectedMonth && <div className="empty">月を選択してください</div>}
          {selectedMonth && loadingDetail && (
            <div className="loading">読込中...</div>
          )}
          {selectedMonth && !loadingDetail && detail && (
            <DetailView
              detail={detail}
              busy={busy}
              onConfirm={handleConfirm}
              onEdit={handleEdit}
              onCancel={handleCancel}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function DetailView({ detail, busy, onConfirm, onEdit, onCancel }) {
  const isConfirmed = detail.confirmed;

  return (
    <>
      <div className="detail-header">
        <h2>
          {detail.year}年{detail.month}月{" "}
          {formatInstructorName(detail.instructor)}
          {isConfirmed && <span className="confirmed-tag">● 確定済</span>}
        </h2>
        <div className="actions">
          {!isConfirmed && (
            <button className="primary" disabled={busy} onClick={onConfirm}>
              確定
            </button>
          )}
          {isConfirmed && (
            <>
              <button className="secondary" disabled={busy} onClick={onEdit}>
                編集（再計算）
              </button>
              <button className="primary" disabled={busy} onClick={onConfirm}>
                再確定（上書き）
              </button>
              <button className="danger" disabled={busy} onClick={onCancel}>
                確定取消
              </button>
            </>
          )}
        </div>
      </div>

      <div className="summary">
        <div className="cell compensation">
          <div className="label">支給合計</div>
          <div className="value">{yen(detail.total_compensation)}</div>
        </div>
        <div className="cell">
          <div className="label">交通費</div>
          <div className="value">{yen(detail.total_trainfare)}</div>
        </div>
        <div className="cell">
          <div className="label">特別交通費</div>
          <div className="value">{yen(detail.special_trainfare)}</div>
        </div>
        <div className="cell">
          <div className="label">源泉徴収</div>
          <div className="value">{yen(detail.withholding_tax)}</div>
        </div>
        <div className="cell">
          <div className="label">出勤日数</div>
          <div className="value">{detail.total_days_worked} 日</div>
        </div>
        <div className="cell">
          <div className="label">休講日数</div>
          <div className="value">{detail.canceled_lessons ?? 0} 日</div>
        </div>
        <div className="cell">
          <div className="label">担当生徒数</div>
          <div className="value">{detail.unique_student_count} 名</div>
        </div>
        {detail.min_wage != null && (
          <div className="cell">
            <div className="label">最低時給</div>
            <div className="value">{yen(detail.min_wage)}</div>
          </div>
        )}
      </div>

      {/* レッスン明細（集計） */}
      {Array.isArray(detail.attendance_data) &&
        detail.attendance_data.length > 0 && (
          <>
            <h4 className="section-title">レッスン明細</h4>
            <table className="lessons">
              <thead>
                <tr>
                  <th>スタジオ</th>
                  <th>レッスン</th>
                  <th>回数</th>
                  <th>出席延べ</th>
                  <th>金額</th>
                </tr>
              </thead>
              <tbody>
                {detail.attendance_data.map((item, idx) => {
                  const totalAttend = (item.lesson_students || []).reduce(
                    (a, [, names]) => a + (names ? names.length : 0),
                    0
                  );
                  return (
                    <tr key={idx}>
                      <td>{item.studio_name}</td>
                      <td>
                        {item.lesson_name}
                        {item.is_beginner_only && (
                          <span className="beginner-badge">未経験者専用</span>
                        )}
                      </td>
                      <td className="r">
                        {(item.lesson_students || []).length +
                          (item.no_enrolled_lesson_count || 0)}
                      </td>
                      <td className="r">{totalAttend}</td>
                      <td className="r">
                        <LessonFeeCell item={item} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

      {/* 所属（生徒×スタジオ×レッスン×プラン） */}
      {(() => {
        const belongRows = flattenBelongStudent(detail.belong_student);
        if (belongRows.length === 0) return null;
        return (
          <>
            <h4 className="section-title">所属（{belongRows.length} 名）</h4>
            <table className="lessons">
              <thead>
                <tr>
                  <th>生徒</th>
                  <th>スタジオ</th>
                  <th>レッスン</th>
                  <th>プラン</th>
                </tr>
              </thead>
              <tbody>
                {belongRows.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.student}</td>
                    <td>{row.studio_name}</td>
                    <td>{row.lesson_name}</td>
                    <td>{row.plan_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        );
      })()}

      {/* 出席詳細（スタジオ/レッスン別） */}
      {(() => {
        const groups = groupAttendanceByLesson(detail.attendance_data);
        if (groups.length === 0) return null;
        const totalEntries = groups.reduce(
          (a, g) => a + g.rows.reduce((b, r) => b + r.students.length, 0),
          0
        );
        return (
          <>
            <h4 className="section-title">
              出席詳細（{totalEntries} 名延べ）
            </h4>
            {groups.map((g, gIdx) => (
              <div className="attend-block" key={gIdx}>
                <div className="attend-title">
                  {g.studio_name} / {g.lesson_name}
                </div>
                <table className="lessons">
                  <thead>
                    <tr>
                      <th style={{ width: 140 }}>日付</th>
                      <th>生徒</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((row, idx) => (
                      <tr key={idx}>
                        <td>{formatDate(row.date)}</td>
                        <td>
                          {row.students.length
                            ? row.students.join("、")
                            : row.note}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </>
        );
      })()}

      {/* 特別手当 */}
      {Array.isArray(detail.special_salaries) &&
        detail.special_salaries.length > 0 && (
          <div className="belong-block">
            <h4>特別手当</h4>
            <table className="lessons">
              <thead>
                <tr>
                  <th>日付</th>
                  <th>項目</th>
                  <th>金額</th>
                  <th>源泉対象</th>
                </tr>
              </thead>
              <tbody>
                {detail.special_salaries.map((s, idx) => (
                  <tr key={idx}>
                    <td>{s.date || "—"}</td>
                    <td>{s.title || "—"}</td>
                    <td className="r">{yen(s.amount)}</td>
                    <td>{s.tax_withholding ? "○" : "×"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

    </>
  );
}

function LessonFeeCell({ item }) {
  // 旧確定済データ（フィールドが無い）への互換: 表示できない場合は「—」
  if (item.lesson_total == null) return <span className="muted">—</span>;
  const isBeginner = !!item.is_beginner_only;
  const minApplied = !!item.min_wage_applied;
  const enrolledCount = (item.lesson_students || []).length;
  // 新フィールド優先・旧確定データは min_wage_only_total にフォールバック
  const noEnrolledTotal = item.no_enrolled_total ?? item.min_wage_only_total;
  // 所属生徒の出席が無い回の内訳（体験者のみ / 出席者なし）
  const details = item.no_enrolled_lesson_details || [];
  const trialDays = details.filter((d) => d[1] === "体験者のみ");
  const noStudentDays = details.filter((d) => d[1] === "出席者なし");
  const trialPeople = trialDays.reduce((a, d) => a + (d[2] || 0), 0);
  const noEnrolledFallbackCount = item.no_enrolled_lesson_count || 0;
  return (
    <div className="lesson-fee-cell">
      <div className={`fee-main ${minApplied ? "min-wage" : ""}`}>
        {yen(item.lesson_total)}
      </div>
      {/* 所属生徒の出席がある回の歩合/最低賃金内訳（出席者なしのみの回は省略） */}
      {enrolledCount > 0 &&
        (minApplied ? (
          <div className="fee-sub">
            最低賃金適用
            {item.plan_fee_subtotal != null && (
              <span className="strike"> 歩合: {yen(item.plan_fee_subtotal)}/回</span>
            )}
          </div>
        ) : (
          item.per_lesson_fee != null && (
            <div className="fee-sub">{yen(item.per_lesson_fee)}/回</div>
          )
        ))}
      {/* 所属生徒の出席が無い回（体験者のみ / 出席者なし） */}
      {isBeginner ? (
        <>
          {/* 未経験者専用: 体験者は生徒として歩合計上（最低賃金なし） */}
          {trialDays.length > 0 && (
            <div className="fee-sub">
              体験者歩合 ×{trialPeople}名
              {noEnrolledTotal != null && ` (${yen(noEnrolledTotal)})`}
            </div>
          )}
          {noStudentDays.length > 0 && (
            <div className="fee-sub muted">
              出席者なし ×{noStudentDays.length}（¥0）
            </div>
          )}
        </>
      ) : (
        // 通常クラス: 出席者なし(全員欠席・体験のみ含む)で最低賃金のみ計上した回
        noEnrolledFallbackCount > 0 && (
          <div className="fee-sub min-wage">
            出席者なし最低賃金 ×{noEnrolledFallbackCount}
            {noEnrolledTotal != null && ` (${yen(noEnrolledTotal)})`}
          </div>
        )
      )}
    </div>
  );
}

export default SalaryConfirmPage;
